import { parseHtml } from "@/utils/common";
import React, { useState } from "react";

type AccordionItem = {
  id: number;
  title?: string;
  answer?: string;
  question?: string;
  displayTitle?: string;
};

interface CAccordionProps {
  data: AccordionItem[];
}

const CAccordion: React.FC<CAccordionProps> = ({ data }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const renderAccordionItem = (item: AccordionItem, index: number) => {
    const isOpen = openIndex === index;
    const isBlue = index % 2 === 0;

    const baseColors = isBlue
      ? "bg-[#F0F7FF] border-[#CCE0FF]"
      : "bg-[#F8FBF3] border-[#DDECCB]";

    const activeColors = isBlue
      ? "bg-[#D6EBFF] border-[#99CCFF]"
      : "bg-[#EAF5DA] border-[#C4E2A3]";

    const activeTextColor = isBlue ? "text-[#0086E6]" : "text-[#2D5016]";

    return (
      <div
        key={index}
        className={`border transition-colors duration-200 min-h-[60px] ${
          isOpen ? activeColors : baseColors
        }`}
      >
        <button
          onClick={() => toggleItem(index)}
          className="w-full flex justify-between items-baseline px-4 py-3 font-bold text-subtitle-1 text-left cursor-pointer"
        >
          <span
            className={`mt-[5px] ${isOpen ? activeTextColor : "text-[#32343B] "}`}
          >
            {item?.question ?? item.title ?? ""}
          </span>
          <span className="text-lg">
            {isOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 12H18"
                  stroke="#333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 12H18"
                  stroke="#333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M12 6V18"
                  stroke="#333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </span>
        </button>

        <div
          style={{
            maxHeight: isOpen ? "500px" : "0px",
            opacity: isOpen ? 1 : 0,
            padding: isOpen ? "1rem" : "0 1rem",
            transition: "all 0.3s ease-in-out",
            overflow: "hidden",
            backgroundColor: "white",
          }}
          className="text-left text-subtitle-2 text-[#32343B] leading-relaxed"
        >
          <div className="accordion-inner">
            {parseHtml(item?.answer ?? item?.displayTitle ?? "")}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 sm:col-span-6 space-y-3">
        {data
          .slice(0, 5)
          .map((item, index) => renderAccordionItem(item, index))}
      </div>
      <div className="col-span-12 sm:col-span-6 space-y-3">
        {data
          .slice(5, 10)
          .map((item, index) => renderAccordionItem(item, index + 5))}
      </div>
    </div>
  );
};

export default CAccordion;
